import { Box, Stack, Typography } from '@mui/material';

import type {
  CalibrationMatrixDistribution,
  Rating,
} from '@/api/types/calibrationMatrix.types';

interface DistributionOfOutcomesSummaryProps {
  distribution: CalibrationMatrixDistribution;
}

const SEGMENT_ORDER: { rating: Rating; key: keyof CalibrationMatrixDistribution; bgcolor: string }[] = [
  { rating: 'BT', key: 'bt', bgcolor: 'custom.distribution.bt' },
  { rating: 'OT', key: 'ot', bgcolor: 'custom.distribution.ot' },
  { rating: 'AT', key: 'at', bgcolor: 'custom.distribution.at' },
  { rating: 'O', key: 'o', bgcolor: 'custom.distribution.o' },
];

export function DistributionOfOutcomesSummary({
  distribution,
}: DistributionOfOutcomesSummaryProps) {
  const totalCount =
    distribution.bt.count +
    distribution.ot.count +
    distribution.at.count +
    distribution.o.count;

  const totalPercent =
    distribution.bt.percentage +
    distribution.ot.percentage +
    distribution.at.percentage +
    distribution.o.percentage;

  return (
    <Box
      role="group"
      aria-label="Distribution of outcomes"
      data-testid="distribution-strip"
      data-total-percent={totalPercent}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Distribution of Outcomes
      </Typography>

      <Stack direction="row" sx={{ width: '100%', mb: 0.5 }}>
        {SEGMENT_ORDER.map(({ rating, key }) => {
          const segment = distribution[key];
          const flexBasis = totalCount === 0 ? 25 : Math.max(segment.percentage, 5);
          return (
            <Box
              key={`label-${rating}`}
              data-testid={`distribution-segment-${rating}`}
              data-segment-percentage={segment.percentage}
              data-segment-count={segment.count}
              role="listitem"
              aria-label={`${rating}: ${segment.percentage}% (${segment.count})`}
              sx={{
                flex: `${flexBasis} 1 0`,
                minWidth: 80,
                px: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" component="span">
                {rating}: {segment.percentage}% ({segment.count})
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Stack
        direction="row"
        sx={{
          width: '100%',
          height: 8,
          borderRadius: 1,
          overflow: 'hidden',
        }}
        role="presentation"
      >
        {SEGMENT_ORDER.map(({ rating, key, bgcolor }) => {
          const segment = distribution[key];
          const flexBasis = totalCount === 0 ? 25 : Math.max(segment.percentage, 5);
          return (
            <Box
              key={`bar-${rating}`}
              sx={{
                flex: `${flexBasis} 1 0`,
                minWidth: 80,
                bgcolor,
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

export default DistributionOfOutcomesSummary;
